import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Real-time password rule checklist. Returns nothing visual when the
 * caller passes `hideWhenEmpty` and `password` is empty.
 *
 * Rules (as agreed with the user):
 *   • At least 8 characters
 *   • At least 1 uppercase letter
 *   • At least 1 lowercase letter
 *   • At least 1 special character (non-alphanumeric)
 */
export function checkPasswordRules(pw = "") {
  return {
    length: pw.length >= 8,
    upper: /[A-ZÇĞİÖŞÜ]/.test(pw),
    lower: /[a-zçğıöşü]/.test(pw),
    special: /[^A-Za-z0-9ÇĞİÖŞÜçğıöşü]/.test(pw),
  };
}

export function isPasswordValid(pw = "") {
  const r = checkPasswordRules(pw);
  return r.length && r.upper && r.lower && r.special;
}

export function PasswordStrength({ password = "", hideWhenEmpty = false, className = "" }) {
  const { t } = useTranslation();
  const r = checkPasswordRules(password);

  if (hideWhenEmpty && !password) return null;

  const items = [
    { ok: r.length, label: t("auth.pw_rule_length") },
    { ok: r.upper, label: t("auth.pw_rule_upper") },
    { ok: r.lower, label: t("auth.pw_rule_lower") },
    { ok: r.special, label: t("auth.pw_rule_special") },
  ];

  return (
    <ul
      className={cn("mt-2 space-y-1", className)}
      data-testid="password-strength-list"
    >
      {items.map((it, i) => (
        <li
          key={i}
          className={cn(
            "flex items-center gap-2 text-xs transition-colors",
            it.ok ? "text-emerald-600" : "text-zinc-500"
          )}
          data-testid={`password-rule-${i}`}
          data-ok={it.ok ? "true" : "false"}
        >
          <span
            className={cn(
              "flex h-4 w-4 flex-none items-center justify-center rounded-full transition-colors",
              it.ok ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-400"
            )}
          >
            {it.ok ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <X className="h-2.5 w-2.5" strokeWidth={3} />}
          </span>
          <span>{it.label}</span>
        </li>
      ))}
    </ul>
  );
}
